<template>
    <div
        ref="containerRef"
        :data-test="dataTestName"
        :class="containerClass"
        v-bind="attrs"
    >
        <transition-group
            name="notify-transition"
            tag="div"
            @enter="onEnter"
            @after-leave="onLeave"
        >
            <NotificationMessage
                v-for="msg of messages"
                :key="msg.id"
                :message="msg"
                @close="remove(msg)"
            />
        </transition-group>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useAttrs } from 'vue'
import { ToastMessageOptions, ToastPositionType } from '@/types/notify'
import ToastEventBus from '@/util/toastEventBus'
import { useUniqueIDWithPrefix } from '@/composables/useUniqueId'
import NotificationMessage from '@/components/RedesignComponents/Notify/NotificationMessage.vue'

type TNotificationPosition = ToastPositionType

const BASE_CLASS = 'app-notifications'

const attrs = useAttrs()

/* Props */
const props = withDefaults(
    defineProps<{
        position?: TNotificationPosition
        group?: string
        baseZIndex?: number
        dataTestName?: string
        teleportedTo?: string | HTMLElement
    }>(),
    {
        position: 'top-right',
        group: undefined,
        baseZIndex: 10000,
        dataTestName: 'default-notify',
        teleportedTo: 'body'
    }
)

/* Data */
const containerRef = ref<HTMLElement>()
const messages = ref<Array<ToastMessageOptions>>([])
const messageIdx = useUniqueIDWithPrefix('notification')
const customPosition = ref<string>('')

/* Computed */
const containerClass = computed(() => {
    const position = customPosition.value || props.position
    return [
        `${BASE_CLASS} ${BASE_CLASS}-component ${BASE_CLASS}-${position}`
    ]
})

/* Methods */
const onEnter = () => {
    const container = containerRef.value
    if (!container) {
        return
    }
    container.style.zIndex = String(props.baseZIndex || 10000)
}
const onLeave = () => {
    const container = containerRef.value
    if (!container) {
        return
    }
    if (container && !messages.value.length) {
        container.style.zIndex = '-1'
    }
}
const add = (message: ToastMessageOptions) => {
    if (message.id == null) {
        message.id = messageIdx()
    }
    messages.value = [ ...messages.value, message ]
}
const remove = (message: ToastMessageOptions) => {
    const _mess = [ ...messages.value ]
    const _index = _mess.findIndex(i => i.id === message.id)
    if (_index >= -1) {
        _mess.splice(_index, 1)
    }
    messages.value = [ ..._mess ]
}
const onAdd = (message: ToastMessageOptions) => {
    if (message.position) {
        customPosition.value = message.position
    }

    if (props.group === message.group) {
        add(message)
    }
}
const onRemoveGroup = (group: string) => {
    if (props.group === group) {
        messages.value = []
    }
}
const onRemoveAllGroups = () => {
    messages.value = []
}


/* Mounted */
onMounted(() => {
    ToastEventBus.on('add', onAdd)
    ToastEventBus.on('remove-group', onRemoveGroup)
    ToastEventBus.on('remove-all-groups', onRemoveAllGroups)
})

/* Unmounted */
onBeforeUnmount(() => {
    ToastEventBus.off('add', onAdd)
    ToastEventBus.off('remove-group', onRemoveGroup)
    ToastEventBus.off('remove-all-groups', onRemoveAllGroups)
})

/* Expose */
defineExpose({
    add,
    remove,
    removeGroup: onRemoveGroup,
    removeAllGroups: onRemoveAllGroups
})
</script>

<style lang="scss">

.app-notifications {
    --toast-width: min(27rem, calc(100% - 2.5rem));
    @apply fixed box-border w-[90vw] md:w-[var(--toast-width)];

    /* Positions */
    &.app-notifications-top-right {
        @apply top-2 right-2 md:top-5 md:right-5;
    }
    &.app-notifications-top-left {
        @apply top-2 left-2 md:top-5 md:left-5;
    }
    &.app-notifications-bottom-left {
        @apply bottom-2 left-2 md:bottom-5 md:left-5;
    }
    &.app-notifications-bottom-right {
        @apply bottom-2 right-2 md:bottom-5 md:right-5;
    }
    &.app-notifications-top-center {
        @apply top-2 md:top-5;
        left: 50%;
        transform: translateX(-50%);
    }
    &.app-notifications-bottom-center {
        @apply bottom-2 md:bottom-5;
        left: 50%;
        transform: translateX(-50%);
    }
    &.app-notifications-center {
        left: 50%;
        top: 50%;
        min-width: 20vw;
        transform: translate(-50%, -50%);
    }

    /* Message */
    .notification-message {
        @apply shadow-card bg-main-bg opacity-90 hover:opacity-100 text-default-text;
        margin: 0 0 1rem 0;
        border-radius: 0 6px 6px 0;
        transition: all .3s ease;

        .notification-message-content {
            @apply flex items-start p-1 sm:p-2 md:p-4;
            border-width: 0 0 0 4px;
            border-color: currentColor;
        }
        .notification-message-text {
            @apply text-default-text pl-3 md:pl-4;
            flex: 1 1 auto;
            .notifications-summary {
                @apply font-semibold text-sm md:text-base mb-2 text-default-text;
            }
            .notifications-detail {
                @apply font-medium text-xs md:text-sm;
                line-height: 145%;
            }
        }
        .notification-message-icon {
            @apply self-center flex-shrink-0 text-current;
        }
        .vc-icon-close {
            @apply text-[14px] md:text-[16px] p-1 sm:p-0;
        }
        .icon-close {
            @apply
            flex
            items-center
            justify-center
            overflow-hidden
            relative
            cursor-pointer
            flex-shrink-0
            text-secondary-actions;
            transition: all .3s ease;
            &:hover {
                @apply text-active-elements--focus;
            }
            &:active {
                @apply text-active-elements--pressed;
            }
        }

        &.notification-message-info {
            @apply text-primary-actions;
        }
        &.notification-message-warn {
            @apply text-orange;
        }
        &.notification-message-error {
            @apply text-red;
        }
        &.notification-message-success {
            @apply text-green;
        }
    }
}

/* Animations */
.notify-transition-move {
    -webkit-transition: transform .3s, opacity .3s;
    transition: transform 0.3s, opacity 0.3s;
}
.notify-transition-enter-active {
    -webkit-transition: transform .3s, opacity .3s;
    transition: transform .3s, opacity .3s;
}
.notify-transition-leave-active {
    -webkit-transition: transform .3s, opacity .3s;
    transition: transform .3s, opacity .3s;
}

.app-notifications-top-right, .app-notifications-bottom-right {
    .notify-transition-enter-from {
        transform: translateX(100%);
    }
}
.app-notifications-top-left, .app-notifications-bottom-left {
    .notify-transition-enter-from {
        transform: translateX(-100%);
    }
}
.app-notifications-center {
    .notify-transition-enter-from {
        transform: translateY(-32px);
    }
}
.app-notifications-bottom-center, .app-notifications-center {
    .notify-transition-enter-from {
        transform: translateY(32px);
    }
}
.notify-transition-enter-from {
    opacity: 0;
    transform: translateX(100%);
}
.notify-transition-leave-to {
    max-height: 0;
    opacity: 0;
    margin-bottom: 0;
    overflow: hidden;
    transform: translateY(-32px);
}

[dir='rtl'] {
    .app-notifications {
        .notification-message {
            border-radius: 6px 0 0 6px;
            .notification-message-content {
                border-width: 0 4px 0 0;
            }
            .notification-message-text {
                @apply pl-1 pr-4;
            }
        }
    }
}
</style>
